import { zodResolver } from "@hookform/resolvers/zod";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Grid2, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
	Controller,
	type DefaultValues,
	FormProvider,
	useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { AddressFormSchema } from "services/AddressService/schemas";
import {
	useGetAllControllersForSelectionMutateAsync,
	useGetAllFacialControllerByLicenseAsync,
} from "services/Devices/Recognition/Controllers/hooks/useMutation";
import { useGetAllRecognitionGroupByLicenseAsync } from "services/Recognition/Group/hooks/useMutations";
import type { ISelectionOption } from "services/types";
import { formatCpf, formatPhone } from "utils/formatters";
import type { z } from "zod";
import ButtonComponent from "../../../../../components/ButtonComponent";
import CheckboxComponent from "../../../../../components/CheckboxComponent";
import DatePickerComponent from "../../../../../components/DatePickerComponent";
import DropdownV2 from "../../../../../components/DropdownComponentV2";
import DropdownSelectComponent from "../../../../../components/DropdownSelectComponent";
import TextFieldComponent from "../../../../../components/TextFieldComponent";
import { AppContext } from "../../../../../context/AppContextProvider";
import { COUNTRYOPTIONS } from "../../../../../services/AddressService/types";
import { useGetAllDeviceGroupByLicenseAsync } from "../../../../../services/DeviceGroupService/hooks/useMutation";
import type { IDeviceGroupSimpleProps } from "../../../../../services/DeviceGroupService/types";
import type { IRecognitionGroupSimpleProps } from "../../../../../services/Recognition/Group/types";
import {
	useCreateResponsibleRecognitionAsync,
	useGetResponsibleRecognitionByIdAsync,
	useUpdateResponsibleRecognitionAsync,
} from "../../../../../services/Recognition/Responsible/hooks/useMutation";
import { ResponsibleRecognitionFormSchema } from "../../../../../services/Recognition/Responsible/schema";
import {
	type IResponsibleRecognitionCreateProps,
	type IResponsibleRecognitionProps,
	type IResponsibleRecognitionUpdateProps,
	ResponsibleGenderTypes,
} from "../../../../../services/Recognition/Responsible/types";
import { useGetAllStudentsForSelectionMutateAsync } from "../../../../../services/Recognition/Student/hooks/useMutation";
import type { IRecognitionSimpleProps } from "../../../../../services/Recognition/types";
import { useGetAllSchoolClassByLicenseAsync } from "../../../../../services/SchoolClass/hooks/useMutation";
import type { ISchoolClassSimpleProps } from "../../../../../services/SchoolClass/types";
import { useGetViaCepAdress } from "../../../../../services/ViaCepService/hooks/useMutation";
import { transformFirebaseLinkToBlob } from "../../../../../utils/TransformUtils";
import { FaceEditionContext } from "../../../context/FaceEditionContextProvider";
import ImageFace from "../cells/ImageFace";
import CarPlate from "./CarPlate";
import { FormChipList } from "./fields/FormChipList";
import VehicleTags from "./fields/VehicleTags";

const DEFAULT_FORM_VALUES: DefaultValues<ResponsibleRecognitionFormInput> = {
	name: "",
	cpf: "",
	image: "",
	gender: "",
	recognitionGroupIds: [],
	facialControllerIds: [],
	deviceGroupIds: [],
	isAddress: false,
	phone: "",
	email: "",
	telegramId: "",
	password: "",
	studentIds: [],
	carPlates: [],
	vehicleTags: null,
	isEnabledEditionPassword: false,
	classId: [],
	deviceToken: "",
	tempStudentsIds: [],
	uc: [],
};

const ONLY_DIGITS_REGEX = /\D/g;

const ADDRESS_DEFAULTS = {
	id: undefined,
	postalCode: "",
	country: "",
	state: "",
	city: "",
	neighborhood: "",
	street: "",
	addrNumber: "",
	complement: "",
};

interface IResponsibleRecognitionEditFormProps {
	selectedRecognitionId?: number;
	handleSaveForm: () => void;
	selectedRecognitionData?: IRecognitionSimpleProps;
}

type ResponsibleRecognitionFormInput = z.infer<
	typeof ResponsibleRecognitionFormSchema
>;
type AddressFormInput = z.infer<typeof AddressFormSchema>;

const ResponsibleRecognitionEditForm = ({
	handleSaveForm,
	selectedRecognitionId,
	selectedRecognitionData,
}: IResponsibleRecognitionEditFormProps) => {
	const { t } = useTranslation();

	const form = useForm<ResponsibleRecognitionFormInput>({
		resolver: zodResolver(ResponsibleRecognitionFormSchema),
		defaultValues: DEFAULT_FORM_VALUES,
	});

	const {
		control,
		reset,
		resetField,
		handleSubmit,
		setValue,
		watch,
		getValues,
		formState: { errors, defaultValues },
	} = form;

	const [groups, setGroups] = useState<IRecognitionGroupSimpleProps[]>();
	const [deviceGroups, setDeviceGroups] = useState<IDeviceGroupSimpleProps[]>();
	const [studentOptions, setStudentOptions] = useState<ISelectionOption[]>([]);
	const [controllers, setControllers] = useState<ISelectionOption[]>([]);
	const [classOptions, setClassOptions] = useState<ISchoolClassSimpleProps[]>(
		[],
	);
	const [address, setAddress] = useState<AddressFormInput>();

	const isAddress = watch("isAddress");
	const isEnabledEditionPassword = watch("isEnabledEditionPassword");

	const { selectedImage, setSelectedImage, isImageChange } =
		useContext(FaceEditionContext);
	const { showSnackbar, setIsShowLoadingComponent } = useContext(AppContext);

	const { mutateAsync: mutateGetViaCepAddress } = useGetViaCepAdress();
	const { mutateAsync: getStudentsSelectionAsync } =
		useGetAllStudentsForSelectionMutateAsync();
	const { mutateAsync: mutateGetClass, isLoading: isLoadingGetClass } =
		useGetAllSchoolClassByLicenseAsync();

	const {
		mutateAsync: mutateGetRecognition,
		isLoading: isLoadingGetRecognition,
	} = useGetResponsibleRecognitionByIdAsync();

	const {
		mutateAsync: mutateCreateRecognition,
		isLoading: isLoadingCreateRecognition,
	} = useCreateResponsibleRecognitionAsync();

	const {
		mutateAsync: mutateUpdateRecognition,
		isLoading: isLoadingUpdateRecognition,
	} = useUpdateResponsibleRecognitionAsync();

	const {
		mutateAsync: mutateGetRecognitionGroups,
		isLoading: isLoadingGetRecognitionGroups,
	} = useGetAllRecognitionGroupByLicenseAsync();

	const {
		mutateAsync: mutateGetDeviceGroups,
		isLoading: isLoadingGetDeviceGroups,
	} = useGetAllDeviceGroupByLicenseAsync();

	const { mutateAsync: mutateGetControllersForSelectionAsync } =
		useGetAllControllersForSelectionMutateAsync();

	const genderOptions = useMemo(
		() => [
			{
				label: t("recognitionPage.modalEditFace.genders.male"),
				value: ResponsibleGenderTypes.MALE,
			},
			{
				label: t("recognitionPage.modalEditFace.genders.female"),
				value: ResponsibleGenderTypes.FEMALE,
			},
			{
				label: t("recognitionPage.modalEditFace.genders.other"),
				value: ResponsibleGenderTypes.OTHER,
			},
		],
		[t],
	);

	const ucOptions = useMemo(
		() => [
			{ label: "Oswaldo Cruz", value: "UNIDADE_1" },
			{ label: "Pindorama", value: "UNIDADE_2" },
			{ label: "Fundação Evangélica", value: "UNIDADE_3" },
		],
		[],
	);

	useEffect(() => {
		const loadDropdownData = async () => {
			try {
				const [
					classData,
					groupsData,
					controllersData,
					deviceGroupsData,
					studentsData,
				] = await Promise.all([
					mutateGetClass(),
					mutateGetRecognitionGroups({}),
					mutateGetControllersForSelectionAsync(),
					mutateGetDeviceGroups(),
					getStudentsSelectionAsync(),
				]);
				setClassOptions(classData || []);
				setGroups(groupsData || []);
				setControllers(controllersData || []);
				setDeviceGroups(deviceGroupsData || []);
				setStudentOptions(studentsData || []);
			} catch (error) {
				console.error("Erro ao carregar listas do form de Responsável", error);
				showSnackbar(t("common.errorLoadingData"), "error");
			}
		};

		loadDropdownData();
	}, []);

	useEffect(() => {
		let isMounted = true;

		if (selectedRecognitionId) {
			mutateGetRecognition(selectedRecognitionId)
				.then(async (response) => {
					if (!isMounted) return;
					const formValues = mapRecognitionToForm(response);
					reset(formValues, { keepDefaultValues: false });

					if (response.image) {
						const image = await transformFirebaseLinkToBlob(response.image);
						if (isMounted) setSelectedImage(image);
					}
				})
				.catch((error) => {
					if (!isMounted) return;
					console.error("Erro ao carregar dados do responsável", error);
					showSnackbar(t("common.errorLoadingData"), "error");
				});
		}

		return () => {
			isMounted = false;
			setSelectedImage("");
		};
	}, [selectedRecognitionId]);

	useEffect(() => {
		if (!selectedRecognitionData) return;

		if (selectedRecognitionData.image) {
			setSelectedImage(selectedRecognitionData.image);
			setValue("name", selectedRecognitionData.name);
		}
		if (selectedRecognitionData.cpf) {
			setValue("cpf", selectedRecognitionData.cpf);
		}
		if (selectedRecognitionData.facialControllersIds) {
			setValue(
				"facialControllerIds",
				selectedRecognitionData.facialControllersIds,
			);
		}
	}, [selectedRecognitionData]);

	useEffect(() => {
		if (!isAddress) {
			setValue("address", null);
		} else if (address) {
			setValue("address", address);
		} else {
			setValue("address", { ...ADDRESS_DEFAULTS, name: "" });
		}
	}, [isAddress, address]);

	const getAddress = useCallback(
		async (value?: string) => {
			if (!value) return;

			try {
				const addressData = await mutateGetViaCepAddress(value);
				setValue("address.postalCode", addressData.postalCode);
				setValue("address.city", addressData.city);
				setValue("address.state", addressData.state);
				setValue("address.street", addressData.street);
				setValue("address.neighborhood", addressData.neighborhood);
			} catch (error) {
				console.error("Erro ao buscar CEP", error);
			}
		},
		[mutateGetViaCepAddress, setValue],
	);

	const convertImageToBase64 = useCallback(
		(image: string | Blob): Promise<string> => {
			return new Promise((resolve, reject) => {
				if (image instanceof Blob) {
					const reader = new FileReader();
					reader.onloadend = () => resolve(reader.result as string);
					reader.onerror = reject;
					reader.readAsDataURL(image);
				} else {
					resolve(image);
				}
			});
		},
		[],
	);

	const buildPayload = useCallback(
		async (data: ResponsibleRecognitionFormInput) => {
			const payload = { ...data };

			if (isImageChange) {
				payload.image = "";
			} else {
				const encodedImage = await convertImageToBase64(
					selectedImage as string | Blob,
				);
				const isFirebaseUrl = encodedImage?.startsWith(
					"https://firebasestorage.googleapis.com/",
				);
				payload.image = isFirebaseUrl ? "" : encodedImage;
			}

			if (payload.validUntil)
				payload.validUntil = payload.validUntil.trim().split("T")[0];
			if (payload.cpf) payload.cpf = payload.cpf.replace(ONLY_DIGITS_REGEX, "");
			if (payload.phone)
				payload.phone = payload.phone.replace(ONLY_DIGITS_REGEX, "");

			return payload;
		},
		[selectedImage, isImageChange, convertImageToBase64],
	);

	const mapRecognitionToForm = (
		response: IResponsibleRecognitionProps,
	): ResponsibleRecognitionFormInput => {
		const hasAddress = !!response.address;

		if (hasAddress) setAddress(response.address);

		const base = {
			id: response.id,
			name: response.name,
			cpf: response.cpf ? formatCpf(response.cpf) : "",
			image: response.image ?? "",
			recognitionGroupIds: response.recognitionGroups?.map((i) => i.id) ?? [],
			facialControllerIds: response.facialControllers?.map((i) => i.id) ?? [],
			deviceGroupIds: response.deviceGroups?.map((i) => i.id) ?? [],
			uc: Array.isArray(response.uc)
				? response.uc
				: response.uc
					? [response.uc]
					: [],
			validUntil: response.validUntil
				? dayjs(response.validUntil).toISOString()
				: null,
			telegramId: response.telegramId ?? "",
			email: response.email ?? "",
			phone: response.phone ? formatPhone(response.phone) : "",
			gender: response.gender,
			password: response.password,
			studentIds: response.studentIds,
			classId: response.classId ?? [],
			carPlates: response.carPlates,
			vehicleTags:
				response.vehicleTags && response.vehicleTags.length > 0
					? response.vehicleTags
					: null,
			deviceToken: response.deviceToken,
			tempStudentsIds: response.tempStudentsIds,
		};

		if (hasAddress) {
			return {
				...base,
				isAddress: true as const,
				address: { ...ADDRESS_DEFAULTS, ...response.address! },
			};
		}

		return {
			...base,
			isAddress: false as const,
			address: null,
		};
	};

	const handleSave = useCallback(
		async (data: ResponsibleRecognitionFormInput) => {
			try {
				const sanitizedData = await buildPayload(data);

				if (data.id) {
					const responsibleUpdated: IResponsibleRecognitionUpdateProps = {
						...sanitizedData,
						vehicleTags: sanitizedData.vehicleTags ?? undefined,
						file: selectedImage as Blob,
					};

					if (!sanitizedData.password || sanitizedData.password.length === 0) {
						responsibleUpdated.password = defaultValues?.password;
					}

					await mutateUpdateRecognition({
						id: data.id,
						data: responsibleUpdated,
					});
					showSnackbar(
						t("recognitionPage.registerFace.updatedFace"),
						"success",
					);
				} else {
					const responsible: IResponsibleRecognitionCreateProps = {
						...sanitizedData,
						vehicleTags: sanitizedData.vehicleTags ?? undefined,
						file: selectedImage as Blob,
					};

					await mutateCreateRecognition(responsible);
					showSnackbar(
						t("recognitionPage.registerFace.registerFace"),
						"success",
					);
				}

				setSelectedImage("");
				reset();
				handleSaveForm();
			} catch (error) {
				console.error("Erro ao salvar responsável", error);
				showSnackbar(t("common.errorSaving"), "error");
			}
		},
		[
			selectedImage,
			buildPayload,
			mutateUpdateRecognition,
			mutateCreateRecognition,
			setSelectedImage,
			reset,
			handleSaveForm,
			showSnackbar,
			defaultValues,
			t,
		],
	);

	return (
		<>
			<Grid2
				container
				direction={"column"}
				justifyContent={"center"}
				alignItems={"center"}
				size={12}
				spacing={3}
			>
				<Grid2
					display={"flex"}
					flexDirection={{ xs: "column", md: "row" }}
					justifyContent={"center"}
					alignItems={{ xs: "center", md: "normal" }}
					size={12}
					gap={{ xs: 0, md: 3 }}
				>
					<Grid2 size={{ xs: 12, lg: 6 }}>
						<Controller
							name="name"
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldComponent
									label={`${t("recognitionPage.modalEditFace.name")} *`}
									onChangeText={onChange}
									type="text"
									errorMessage={error?.message}
									value={value}
								/>
							)}
						/>

						<Controller
							name="cpf"
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldComponent
									label={`${t("recognitionPage.modalEditFace.cpf")} *`}
									onChangeText={onChange}
									type="text"
									errorMessage={error?.message}
									value={value}
									mask="000.000.000-00"
								/>
							)}
						/>

						<Controller
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldComponent
									label={t("recognitionPage.modalEditFace.email")}
									onChangeText={(val) => onChange(val)}
									type={"text"}
									errorMessage={error?.message}
									value={value}
								/>
							)}
							name={"email"}
							control={control}
						/>

						<Controller
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => {
								const isPasswordRequired =
									!selectedRecognitionId || isEnabledEditionPassword;
								const label = `${t("recognitionPage.modalEditFace.password")}${isPasswordRequired ? " *" : ""}`;

								return (
									<Box display={"flex"}>
										<TextFieldComponent
											label={label}
											onChangeText={(val) => onChange(val)}
											type={"password"}
											isVisiblePassword={isPasswordRequired}
											disabled={
												selectedRecognitionId != undefined &&
												!isEnabledEditionPassword
											}
											errorMessage={error?.message}
											value={value}
										/>
										{selectedRecognitionId && (
											<ButtonComponent
												sx={{
													borderTopLeftRadius: 0,
													borderBottomLeftRadius: 0,
													height: "70%",
												}}
												iconOnly
												onClick={() => {
													if (isEnabledEditionPassword) resetField("password");
													else setValue("password", "");
													setValue(
														"isEnabledEditionPassword",
														!isEnabledEditionPassword,
													);
												}}
												tooltipMessage={t(
													"recognitionPage.actionButtonComponent.editPassword",
												)}
											>
												<EditIcon />
											</ButtonComponent>
										)}
									</Box>
								);
							}}
							name={"password"}
							control={control}
						/>

						{(!selectedRecognitionId ||
							(selectedRecognitionId && isEnabledEditionPassword)) && (
							<Controller
								render={({
									field: { value, onChange },
									fieldState: { error },
								}) => (
									<TextFieldComponent
										label={`${t("recognitionPage.modalEditFace.repeatPassword")} *`}
										onChangeText={(val) => onChange(val)}
										type={"password"}
										disabled={
											selectedRecognitionId != undefined &&
											!isEnabledEditionPassword
										}
										errorMessage={error?.message}
										value={value}
									/>
								)}
								name={"repeatPassword"}
								control={control}
							/>
						)}

						<Controller
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DatePickerComponent
									value={value ? dayjs(value).utc() : null}
									onChange={(value) => {
										const date = dayjs(value);
										onChange(
											date.isValid() ? dayjs(value).toISOString() : null,
										);
									}}
									errorMessage={error?.message}
									label={t("recognitionPage.modalEditFace.faceValidity")}
									isOptional={true}
									disablePast={true}
								/>
							)}
							name={"validUntil"}
							control={control}
						/>
						<FormProvider {...form}>
							<CarPlate />
							<VehicleTags />
						</FormProvider>

						<Controller
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownSelectComponent
									label={t("recognitionPage.modalEditFace.temporaryStudents")}
									selectedValues={value as any[]}
									onSelectedValues={onChange}
									options={studentOptions}
									labelField={"name"}
									valueField={"id"}
								/>
							)}
							name={"tempStudentsIds"}
							control={control}
						/>
					</Grid2>
					<Grid2
						size={{ xs: 12, lg: 6 }}
						display={"column"}
						alignItems={"center"}
						justifyContent={"center"}
					>
						<Controller
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownSelectComponent
									label={t("recognitionPage.modalEditFace.faceGroup")}
									options={groups}
									labelField={"name"}
									valueField={"id"}
									errorMessage={error?.message}
									selectedValues={value}
									onSelectedValues={onChange}
								/>
							)}
							name={"recognitionGroupIds"}
							control={control}
						/>
						<Controller
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownSelectComponent
									label={t("recognitionPage.modalEditFace.facialController")}
									options={controllers}
									labelField={"name"}
									valueField={"id"}
									errorMessage={error?.message}
									selectedValues={value}
									onSelectedValues={onChange}
								/>
							)}
							name={"facialControllerIds"}
							control={control}
						/>
						<Controller
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownSelectComponent
									label={t("recognitionPage.modalEditFace.deviceGroup")}
									options={deviceGroups}
									labelField={"name"}
									valueField={"id"}
									errorMessage={error?.message}
									selectedValues={value}
									onSelectedValues={onChange}
								/>
							)}
							name={"deviceGroupIds"}
							control={control}
						/>

						<Controller
							name={"classId"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error, invalid },
							}) => (
								<DropdownSelectComponent
									options={classOptions}
									label={t("recognitionPage.modalEditFace.class")}
									labelField={"name"}
									valueField={"id"}
									error={invalid}
									errorMessage={error?.message}
									selectedValues={value as any[]}
									onSelectedValues={onChange}
								/>
							)}
						/>

						<Controller
							name="gender"
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownV2
									label={`${t("recognitionPage.modalEditFace.genders.gender")} *`}
									value={value}
									errorMessage={error?.message}
									optionLabel="label"
									optionValue="value"
									options={genderOptions}
									onChange={onChange}
								/>
							)}
						/>

						<Controller
							name={"studentIds"}
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error, invalid },
							}) => {
								const safeValues = Array.isArray(value) ? value : [];

								const handleRemove = (idToRemove: number | string) => {
									onChange(safeValues.filter((id) => id !== idToRemove));
								};

								return (
									<Box sx={{ width: "100%" }}>
										<DropdownSelectComponent
											options={studentOptions}
											label={t("recognitionPage.registerFace.student")}
											labelField={"name"}
											valueField={"id"}
											error={invalid}
											errorMessage={error?.message}
											selectedValues={safeValues}
											onSelectedValues={onChange}
										/>
										{safeValues.length > 0 && (
											<FormChipList
												options={studentOptions}
												selectedIds={safeValues}
												valueField="id"
												labelField="name"
												onRemove={handleRemove}
											/>
										)}
									</Box>
								);
							}}
						/>

						<Controller
							name="phone"
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldComponent
									label={`${t("recognitionPage.modalEditFace.cellPhone")} *`}
									onChangeText={onChange}
									type="text"
									errorMessage={error?.message}
									value={value}
									mask="(00) 0 0000-0000"
									maxLength={16}
								/>
							)}
						/>

						<Controller
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<TextFieldComponent
									label="Telegram ID"
									onChangeText={(val) => onChange(val)}
									type={"text"}
									errorMessage={error?.message}
									value={value}
								/>
							)}
							name={"telegramId"}
							control={control}
						/>
						<Controller
							name="uc"
							control={control}
							render={({
								field: { value, onChange },
								fieldState: { error },
							}) => (
								<DropdownSelectComponent
									label={t("recognitionPage.modalEditFace.corporateUnity")}
									options={ucOptions}
									labelField="label"
									valueField="value"
									errorMessage={error?.message}
									selectedValues={Array.isArray(value) ? value : []}
									onSelectedValues={onChange}
								/>
							)}
						/>

						<Grid2
							display={"flex"}
							justifyContent={"center"}
							alignItems={"center"}
							mt={4}
						>
							<Controller
								name="isAddress"
								control={control}
								render={({ field: { value, onChange } }) => (
									<CheckboxComponent
										label={t("recognitionPage.modalEditFace.addAddress")}
										value={value}
										onChange={onChange}
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</Grid2>
				{isAddress && (
					<>
						<Grid2 display={"flex"} justifyContent={"start"} width={"100%"}>
							<Typography variant={"h5"} component={"h2"} fontWeight={"bolder"}>
								{t("recognitionPage.modalEditFace.address.address")}
							</Typography>
						</Grid2>
						<Grid2
							display={"flex"}
							flexDirection={{ xs: "column", md: "row" }}
							justifyContent={"center"}
							alignItems={{ xs: "center", md: "normal" }}
							size={12}
							gap={{ xs: 0, md: 3 }}
						>
							<Grid2 size={{ xs: 12, lg: 6 }}>
								<Controller
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldComponent
											label={t(
												"recognitionPage.modalEditFace.address.postalCode",
											)}
											onChangeText={(val) => onChange(val)}
											type={"text"}
											onBlur={() => getAddress(value)}
											errorMessage={error?.message}
											value={value}
											mask={"00000-000"}
										/>
									)}
									name={"address.postalCode"}
									control={control}
								/>
								<Controller
									name={"address.country"}
									control={control}
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<DropdownV2
											label={t("recognitionPage.modalEditFace.address.country")}
											options={COUNTRYOPTIONS}
											optionLabel={"label"}
											optionValue={"value"}
											errorMessage={error?.message}
											value={value}
											onChange={onChange}
										/>
									)}
								/>

								<Controller
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldComponent
											label={t("recognitionPage.modalEditFace.address.state")}
											onChangeText={(val) => onChange(val)}
											type={"text"}
											errorMessage={error?.message}
											value={value}
										/>
									)}
									name={"address.state"}
									control={control}
								/>
								<Controller
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldComponent
											label={t("recognitionPage.modalEditFace.address.city")}
											onChangeText={(val) => onChange(val)}
											type={"text"}
											errorMessage={error?.message}
											value={value}
										/>
									)}
									name={"address.city"}
									control={control}
								/>
							</Grid2>
							<Grid2
								size={{ xs: 12, lg: 6 }}
								display={"column"}
								alignItems={"center"}
								justifyContent={"center"}
							>
								<Controller
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldComponent
											label={t(
												"recognitionPage.modalEditFace.address.neighborhood",
											)}
											onChangeText={(val) => onChange(val)}
											type={"text"}
											errorMessage={error?.message}
											value={value}
										/>
									)}
									name={"address.neighborhood"}
									control={control}
								/>
								<Controller
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldComponent
											label={t("recognitionPage.modalEditFace.address.street")}
											onChangeText={(val) => onChange(val)}
											type={"text"}
											errorMessage={error?.message}
											value={value}
										/>
									)}
									name={"address.street"}
									control={control}
								/>
								<Controller
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldComponent
											label={t(
												"recognitionPage.modalEditFace.address.addrNumber",
											)}
											onChangeText={(val) => onChange(val)}
											type={"text"}
											isOptional={true}
											errorMessage={error?.message}
											value={value}
										/>
									)}
									name={"address.addrNumber"}
									control={control}
								/>
								<Controller
									render={({
										field: { value, onChange },
										fieldState: { error },
									}) => (
										<TextFieldComponent
											label={t(
												"recognitionPage.modalEditFace.address.complement",
											)}
											isOptional={true}
											onChangeText={(val) => onChange(val)}
											type={"text"}
											errorMessage={error?.message}
											value={value}
										/>
									)}
									name={"address.complement"}
									control={control}
								/>
							</Grid2>
						</Grid2>
					</>
				)}
				<Grid2
					size={8}
					display={"column"}
					alignItems={"center"}
					justifyContent={"center"}
					mb={3}
				>
					<ImageFace handleCancelImageEdition={() => setSelectedImage("")} />
				</Grid2>
			</Grid2>
			<ButtonComponent
				variant="contained"
				color="primary"
				loading={isLoadingUpdateRecognition || isLoadingCreateRecognition}
				fullWidth
				onClick={handleSubmit(handleSave)}
			>
				{t("recognitionPage.modalEditFace.buttonCaptureImage.save")}
			</ButtonComponent>
		</>
	);
};

export default ResponsibleRecognitionEditForm;
